import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip as ChartTooltip,
    Legend as ChartLegend,
} from 'chart.js';
import { formatNumber, formatDate, getDaysInStockColor } from '../../../utils/formatting';
import apiService from '../../../services/apiService';
import Sparkline from '../../Sparkline';
import { unitAgingBucketId, unitAgingLabel } from '../../../utils/dealershipSettings';
import { useDealershipSettings } from '../../../contexts/settingsContext';
import SOURCE_LABELS from '../../../config/sourceLabels';
import {
    bounceText, bounceTone, completenessTone, ctrStatus, formatSession, last30, leadTone, monthlyBuckets, personLabel, sessionTone,
} from '../../../utils/unitOverview';
import './IronSightCards.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, ChartLegend);

function OverviewTab({ unit, mfData, mfLoading }) {
    const { aging } = useDealershipSettings();
    const ageId = unitAgingBucketId(unit, aging);
    const ageLabel = unitAgingLabel(unit, aging);
    const [thDetail, setThDetail] = useState(null);
    const [thDetailLoading, setThDetailLoading] = useState(false);
    const [ga4Detail, setGa4Detail] = useState(null);
    const [ga4Loading, setGa4Loading] = useState(false);

    // Fetch the full TractorHouse detail (including contact action metrics) when
    // the unit has an active TH listing. The inventory list only carries the top-3
    // metrics; watchlists and click-to-contact counts require the detail endpoint.
    useEffect(() => {
        if (!unit?.StockNumber || !unit?.THStatus) {
            setThDetail(null);
            return;
        }
        let cancelled = false;
        setThDetailLoading(true);
        apiService.getUnitTractorHouse(unit.StockNumber)
            .then((data) => { if (!cancelled) setThDetail(data); })
            .catch(() => { if (!cancelled) setThDetail(null); })
            .finally(() => { if (!cancelled) setThDetailLoading(false); });
        return () => { cancelled = true; };
    }, [unit?.StockNumber, unit?.THStatus]);

    // Fetch GA4 engagement data when the unit has a matching website page path.
    useEffect(() => {
        if (!unit?.StockNumber || !unit?.GAPagePath) {
            setGa4Detail(null);
            return;
        }
        let cancelled = false;
        setGa4Loading(true);
        apiService.getUnitGA4(unit.StockNumber)
            .then((data) => { if (!cancelled) setGa4Detail(data); })
            .catch(() => { if (!cancelled) setGa4Detail(null); })
            .finally(() => { if (!cancelled) setGa4Loading(false); });
        return () => { cancelled = true; };
    }, [unit?.StockNumber, unit?.GAPagePath]);

    const getStatusColor = getDaysInStockColor;

    // Today's rules (utils/unitOverview, shared with the full unit page).
    const COMPLETENESS_COLORS = { good: '#15803d', warn: '#b45309', poor: '#b91c1c' };
    const getCompletenessColor = (pct) => COMPLETENESS_COLORS[completenessTone(pct)];

    // TractorHouse helpers
    const CTR_CLASSES = { good: 'th-ctr-good', ok: 'th-ctr-ok', poor: 'th-ctr-poor' };
    const getTHCtrClass = (impressions, detailViews) => CTR_CLASSES[ctrStatus(impressions, detailViews).tone];

    const getTHCtrValue = (impressions, detailViews) => ctrStatus(impressions, detailViews).value;

    const getTHLeadClass = (leads, daysInStock) => {
        const tone = leadTone(leads, daysInStock);
        if (tone === 'good') return 'th-metric-value th-metric-good';
        if (tone === 'poor') return 'th-metric-value th-metric-poor';
        return 'th-metric-value';
    };

    const hasTH = !!unit.THStatus;

    // Monthly buckets of the TH / GA4 daily_history for the trend charts.
    const monthlyChart = useMemo(() => {
        const m = monthlyBuckets(thDetail?.daily_history, ['impressions', 'details_views', 'email_leads']);
        return m && {
            labels: m.labels, impressions: m.series.impressions, views: m.series.details_views, monthCount: m.monthCount,
        };
    }, [thDetail]);

    const ga4MonthlyChart = useMemo(() => {
        const m = monthlyBuckets(ga4Detail?.daily_history, ['pageviews', 'users']);
        return m && {
            labels: m.labels, pageviews: m.series.pageviews, users: m.series.users, monthCount: m.monthCount,
        };
    }, [ga4Detail]);

    return (
        <div className="tab-content">
            <section className="detail-section is-card">
                <div className="is-card-body">
                <h3 className="is-section-title">Equipment Details</h3>
                <div className="detail-grid">
                    <div className="detail-item"><label>Make</label><span>{unit.MAKE}</span></div>
                    <div className="detail-item"><label>Model</label><span>{unit.MODEL}</span></div>
                    <div className="detail-item"><label>Category</label><span>{unit.CATEGORY}</span></div>
                    <div className="detail-item"><label>Year</label><span>{unit.Model_Year} (Manuf: {unit.YEAR_MANUF})</span></div>
                    <div className="detail-item"><label>Condition</label><span>{unit.NewUsedStatus}</span></div>
                    <div className="detail-item">
                        <label>Stock Status</label>
                        <span>
                            {unit.StockStatus === 'P' && <span className="stock-status-badge stock-status-pending">Pending</span>}
                            {unit.StockStatus === 'S' && <span className="stock-status-badge stock-status-instock">In Stock</span>}
                            {!unit.StockStatus && '—'}
                        </span>
                    </div>
                    <div className="detail-item"><label>Branch</label><span>{unit.BranchName ?? unit.LOCATION ?? '—'}</span></div>
                </div>
                </div>
            </section>

            <section className="detail-section is-card">
                <div className="is-card-body">
                <h3 className="is-section-title">Aging &amp; Inventory</h3>
                <div className="detail-grid">
                    <div className="detail-item">
                        <label>Date Received</label>
                        <span>{formatDate(unit.DATEREC)}</span>
                    </div>
                    <div className="detail-item">
                        <label>Days in Stock</label>
                        <span className="days-value" style={{ color: getStatusColor(ageId) }}>
                            {unit.DaysInStock} days
                        </span>
                    </div>
                    <div className="detail-item full-width">
                        <label>Aging Bucket</label>
                        <span className="bucket-badge" style={{
                            backgroundColor: getStatusColor(ageId) + '20',
                            color: getStatusColor(ageId),
                            border: `1px solid ${getStatusColor(ageId)}`
                        }}>
                            {ageLabel}
                        </span>
                    </div>
                </div>
                </div>
            </section>

            <section className="detail-section is-card">
                <div className="is-card-body">
                <h3 className="is-section-title">Assignments</h3>
                <div className="detail-grid">
                    <div className="detail-item"><label>Salesperson</label><span>{unit.Salesman || 'Unassigned'}</span></div>
                    <div className="detail-item"><label>Customer</label><span>{unit.Customer || 'None'}</span></div>
                </div>
                </div>
            </section>

            <section className="detail-section is-card">
                <div className="is-card-body">
                <h3 className="is-section-title">Acquisition</h3>
                <div className="detail-grid">
                    <div className="detail-item"><label>Acquired From</label><span>{personLabel(unit.AcquiredBy_FullName)}</span></div>
                    <div className="detail-item"><label>Previous Driver</label><span>{personLabel(unit.PrevDriver_FullName)}</span></div>
                </div>
                </div>
            </section>

            {mfLoading && (
                <section className="detail-section">
                    <div className="mf-loading">Loading {SOURCE_LABELS.listingLower} data...</div>
                </section>
            )}

            {mfData && (
                <>
                    {mfData.description && (
                        <section className="detail-section is-card">
                            <div className="is-card-body">
                            <h3 className="is-section-title">Listing Description</h3>
                            <p className="mf-description">{mfData.description}</p>
                            </div>
                        </section>
                    )}

                    {mfData.options.length > 0 && (
                        <section className="detail-section is-card">
                            <div className="is-card-body">
                            <h3 className="is-section-title">Options &amp; Attachments</h3>
                            <div className="mf-options">
                                {mfData.options.map((opt, i) => (
                                    <span key={i} className="mf-option-tag">{opt}</span>
                                ))}
                            </div>
                            </div>
                        </section>
                    )}

                    <section className="detail-section is-card">
                        <div className="is-card-body">
                        <h3 className="is-section-title">{SOURCE_LABELS.listingDetails}</h3>
                        <div className="detail-grid">
                            {mfData.serial_number && (
                                <div className="detail-item"><label>Serial Number</label><span>{mfData.serial_number}</span></div>
                            )}
                            {mfData.operation_hours != null && (
                                <div className="detail-item"><label>Operation Hours (Listing)</label><span>{formatNumber(mfData.operation_hours)}</span></div>
                            )}
                            {mfData.horse_power != null && (
                                <div className="detail-item"><label>Horsepower</label><span>{mfData.horse_power} HP</span></div>
                            )}
                            {mfData.status_key && (
                                <div className="detail-item"><label>Listing Status</label><span className="mf-status-badge">{mfData.status_key}</span></div>
                            )}
                            {mfData.is_new != null && (
                                <div className="detail-item"><label>Condition</label><span>{mfData.is_new ? 'New' : 'Used'}</span></div>
                            )}
                        </div>
                        </div>
                    </section>

                    <section className="detail-section is-card">
                        <div className="is-card-body">
                        <h3 className="is-section-title">Listing Quality</h3>
                        <div className="mf-completeness">
                            <div className="mf-completeness-bar-wrap">
                                <div className="mf-completeness-bar" style={{
                                    width: `${mfData.completeness_pct}%`,
                                    backgroundColor: getCompletenessColor(mfData.completeness_pct)
                                }}></div>
                            </div>
                            <span className="mf-completeness-pct" style={{ color: getCompletenessColor(mfData.completeness_pct) }}>
                                {mfData.completeness_score}/6 fields complete
                            </span>
                            {mfData.missing_fields.length > 0 && (
                                <p className="mf-completeness-missing">
                                    Missing: {mfData.missing_fields.map((f, i) => (
                                        <span key={i} className="mf-missing-tag">{f}</span>
                                    ))}
                                </p>
                            )}
                        </div>
                        </div>
                    </section>
                </>
            )}

            {hasTH && renderTractorHouseSection({
                unit,
                thDetail,
                thDetailLoading,
                monthlyChart,
                getTHCtrClass,
                getTHCtrValue,
                getTHLeadClass,
            })}

            {unit?.GAPagePath && renderGA4Section({
                unit,
                ga4Detail,
                ga4Loading,
                ga4MonthlyChart,
            })}
        </div>
    );
}

OverviewTab.propTypes = {
    unit: PropTypes.object.isRequired,
    mfData: PropTypes.object,
    mfLoading: PropTypes.bool,
};

OverviewTab.defaultProps = {
    mfData: null,
    mfLoading: false,
};

/**
 * Renders the unified TractorHouse Performance section.
 * All metrics come from the detail endpoint response (single source of truth,
 * no staggered load). Falls back to a skeleton while the detail request is
 * in flight. The 30-day numbers come from thDetail.metrics_30d; the chart
 * aggregates thDetail.daily_history into monthly totals.
 */
function renderTractorHouseSection({
    unit,
    thDetail,
    thDetailLoading,
    monthlyChart,
    getTHCtrClass,
    getTHCtrValue,
    getTHLeadClass,
}) {
    // Skeleton while loading — keeps the card visible but animates placeholders
    if (thDetailLoading && !thDetail) {
        return (
            <section className="detail-section th-section">
                <div className="th-section-header">
                    <h3>
                        <span className="th-title-accent">{SOURCE_LABELS.marketplace}</span> Performance
                        <span className="th-window-label">(30 days)</span>
                    </h3>
                </div>
                <div className="th-skeleton">
                    <div className="th-skeleton-row" />
                    <div className="th-skeleton-row" />
                </div>
            </section>
        );
    }

    if (!thDetail) return null;

    const m30 = thDetail.metrics_30d || {};
    const impressions = m30.impressions ?? 0;
    const views = m30.details_views ?? 0;
    const leads = m30.email_leads ?? 0;
    const watchlists = m30.total_watchlists ?? 0;
    const callsClicks = m30.clicks_to_call ?? 0;
    const textsClicks = m30.clicks_to_text ?? 0;

    const statusActive = thDetail.status === 'ACTIVE';
    const isFeatured = !!thDetail.is_featured;
    const listingUrl = thDetail.listing_url;

    const ctrClass = getTHCtrClass(impressions, views);
    const ctrValue = getTHCtrValue(impressions, views);
    const ctrLabel = ctrClass === 'th-ctr-good' ? 'Good CTR'
        : ctrClass === 'th-ctr-ok' ? 'Low CTR'
            : 'Very low CTR';

    // 30-day sparkline arrays (oldest → newest)
    const sparkImpressions = last30(thDetail.daily_history, 'impressions');
    const sparkViews = last30(thDetail.daily_history, 'details_views');
    const sparkLeads = last30(thDetail.daily_history, 'email_leads');
    const SAG_GREEN = '#6f8f5c';
    const TH_AMBER = '#b45309';

    return (
        <section className="detail-section th-section">
            {/* Header with title + status badges */}
            <div className="th-section-header">
                <h3>
                    <span className="th-title-accent">{SOURCE_LABELS.marketplace}</span> Performance
                    <span className="th-window-label">(30 days)</span>
                </h3>
                <div className="th-badges">
                    <span className={statusActive ? 'th-status-active' : 'th-status-deleted'}>
                        {statusActive ? 'Active' : 'Deleted'}
                    </span>
                    {isFeatured && (
                        <span className="th-featured-badge">
                            <svg viewBox="0 0 20 20" fill="currentColor" width="11" height="11" aria-hidden="true">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            Featured
                        </span>
                    )}
                </div>
            </div>

            {/* Primary metrics — Impressions / Views / Leads / CTA link */}
            <div className="th-metrics-grid">
                <div className="th-metric-card th-metric-card-primary">
                    <div className="th-metric-label">
                        <span className="th-metric-icon">👁</span>
                        Impressions
                    </div>
                    <div className="th-metric-value">{formatNumber(impressions)}</div>
                    {sparkImpressions.length > 1 && <Sparkline values={sparkImpressions} color={SAG_GREEN} />}
                    {impressions === 0 && <div className="th-metric-hint">No recent activity.</div>}
                </div>

                <div className="th-metric-card th-metric-card-primary">
                    <div className="th-metric-label">
                        <span className="th-metric-icon">🔍</span>
                        Details Views
                    </div>
                    <div className="th-metric-value">{formatNumber(views)}</div>
                    {sparkViews.length > 1 && <Sparkline values={sparkViews} color={SAG_GREEN} />}
                    {ctrValue && (
                        <div className={`th-ctr-chip ${ctrClass}`}>
                            {ctrValue}% CTR — {ctrLabel}
                        </div>
                    )}
                </div>

                <div className="th-metric-card th-metric-card-primary th-metric-card-leads">
                    <div className="th-metric-label">
                        <span className="th-metric-icon">✉️</span>
                        Email Leads
                    </div>
                    <div className={getTHLeadClass(leads, unit.DaysInStock)}>{leads}</div>
                    {sparkLeads.length > 1 && <Sparkline values={sparkLeads} color={TH_AMBER} />}
                </div>

                {listingUrl && (
                    <div className="th-metric-card th-link-card">
                        <a href={listingUrl} target="_blank" rel="noopener noreferrer" className="th-listing-link">
                            View listing
                            <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13" aria-hidden="true" style={{ marginLeft: 5 }}>
                                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                                <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                            </svg>
                        </a>
                    </div>
                )}
            </div>

            {/* Contact & Save actions */}
            <div className="th-subsection">
                <div className="th-subsection-title">Contact & Save Actions (30 days)</div>
                <div className="th-actions-grid">
                    <div className="th-action-card">
                        <div className="th-action-icon">📞</div>
                        <div className="th-action-body">
                            <div className="th-action-value">{formatNumber(callsClicks)}</div>
                            <div className="th-action-label">Calls</div>
                        </div>
                    </div>
                    <div className="th-action-card">
                        <div className="th-action-icon">💬</div>
                        <div className="th-action-body">
                            <div className="th-action-value">{formatNumber(textsClicks)}</div>
                            <div className="th-action-label">Texts</div>
                        </div>
                    </div>
                    <div className="th-action-card">
                        <div className="th-action-icon">⭐</div>
                        <div className="th-action-body">
                            <div className="th-action-value">{formatNumber(watchlists)}</div>
                            <div className="th-action-label">Saved</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Monthly trend chart */}
            {monthlyChart && monthlyChart.monthCount >= 2 && (
                <div className="th-subsection">
                    <div className="th-subsection-title">
                        Monthly Trend
                        <span className="th-chart-sublabel"> — last {monthlyChart.monthCount} {monthlyChart.monthCount === 1 ? 'month' : 'months'}</span>
                    </div>
                    <div className="th-chart-wrap">
                        <Bar
                            data={{
                                labels: monthlyChart.labels,
                                datasets: [
                                    {
                                        label: 'Impressions',
                                        data: monthlyChart.impressions,
                                        backgroundColor: 'rgba(111, 143, 92, 0.35)',
                                        borderColor: '#6f8f5c',
                                        borderWidth: 1.5,
                                        borderRadius: 4,
                                        yAxisID: 'y',
                                    },
                                    {
                                        label: 'Details Views',
                                        data: monthlyChart.views,
                                        backgroundColor: 'rgba(29, 78, 216, 0.55)',
                                        borderColor: '#1d4ed8',
                                        borderWidth: 1.5,
                                        borderRadius: 4,
                                        yAxisID: 'y1',
                                    },
                                ],
                            }}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                interaction: { mode: 'index', intersect: false },
                                plugins: {
                                    legend: { position: 'top', labels: { boxWidth: 12, font: { size: 11 } } },
                                    tooltip: {
                                        callbacks: {
                                            label: (ctx) => `${ctx.dataset.label}: ${formatNumber(ctx.parsed.y || 0)}`,
                                        },
                                    },
                                },
                                scales: {
                                    x: { grid: { display: false }, ticks: { font: { size: 10 } } },
                                    y: {
                                        position: 'left',
                                        beginAtZero: true,
                                        title: { display: true, text: 'Impressions', font: { size: 10 } },
                                        ticks: { font: { size: 10 } },
                                    },
                                    y1: {
                                        position: 'right',
                                        beginAtZero: true,
                                        title: { display: true, text: 'Views', font: { size: 10 } },
                                        grid: { drawOnChartArea: false },
                                        ticks: { font: { size: 10 } },
                                    },
                                },
                            }}
                        />
                    </div>
                </div>
            )}
        </section>
    );
}

/**
 * Renders the Gooseneck Website Engagement (GA4) section.
 * Visibility is guarded upstream: only called when unit.GAPagePath is truthy.
 * Mirrors renderTractorHouseSection in structure and skeleton behaviour.
 */
function renderGA4Section({ ga4Detail, ga4Loading, ga4MonthlyChart }) {
    // Skeleton while loading
    if (ga4Loading && !ga4Detail) {
        return (
            <section className="detail-section ga-section">
                <div className="ga-section-header">
                    <h3>
                        <span className="ga-title-accent">{SOURCE_LABELS.website}</span> Engagement
                        <span className="ga-window-label">(30 days)</span>
                    </h3>
                </div>
                <div className="ga-skeleton">
                    <div className="ga-skeleton-row" />
                    <div className="ga-skeleton-row" />
                </div>
            </section>
        );
    }

    if (!ga4Detail) return null;

    const m30 = ga4Detail.metrics_30d || {};
    const pageviews = m30.pageviews ?? 0;
    const users = m30.users ?? 0;
    const avgSessionSec = m30.avg_session_sec ?? 0;
    const bounceRate = m30.bounce_rate ?? 0;
    const listingUrl = ga4Detail.listing_url;

    // Traffic-light classes for session duration
    const sessionClass = sessionTone(avgSessionSec)
        ? `ga-metric-value ga-metric-${sessionTone(avgSessionSec)}`
        : 'ga-metric-value';

    // Traffic-light classes for bounce rate (<0.4 green, 0.4-0.7 amber, >0.7 red)
    const bounceClass = `ga-metric-value ga-metric-${bounceTone(bounceRate)}`;

    // Build 30-day sparkline arrays from daily_history (oldest → newest)
    const sparkPageviews = last30(ga4Detail.daily_history, 'pageviews');
    const sparkUsers = last30(ga4Detail.daily_history, 'users');
    const sparkSession = last30(ga4Detail.daily_history, 'avg_session_sec');
    const sparkBounce = last30(ga4Detail.daily_history, 'bounce_rate');
    const GA_BLUE = '#4285f4';

    return (
        <section className="detail-section ga-section">
            <div className="ga-section-header">
                <h3>
                    <span className="ga-title-accent">{SOURCE_LABELS.website}</span> Engagement
                    <span className="ga-window-label">(30 days)</span>
                </h3>
            </div>

            <div className="ga-metrics-grid">
                {/* Pageviews */}
                <div className="ga-metric-card ga-metric-card-primary">
                    <div className="ga-metric-label">
                        <span className="ga-metric-icon">👁</span>
                        Pageviews
                        <span
                            className="ga-info-tip"
                            title="Times the product page was loaded"
                            aria-label="Times the product page was loaded"
                        >?</span>
                    </div>
                    <div className="ga-metric-value">{formatNumber(pageviews)}</div>
                    {sparkPageviews.length > 1 && <Sparkline values={sparkPageviews} color={GA_BLUE} />}
                </div>

                {/* Unique Users */}
                <div className="ga-metric-card ga-metric-card-primary">
                    <div className="ga-metric-label">
                        <span className="ga-metric-icon">👤</span>
                        Unique Users
                        <span
                            className="ga-info-tip"
                            title="Distinct visitors (approx.)"
                            aria-label="Distinct visitors (approx.)"
                        >?</span>
                    </div>
                    <div className="ga-metric-value">{formatNumber(users)}</div>
                    {sparkUsers.length > 1 && <Sparkline values={sparkUsers} color={GA_BLUE} />}
                </div>

                {/* Avg Session — traffic light */}
                <div className="ga-metric-card ga-metric-card-primary">
                    <div className="ga-metric-label">
                        <span className="ga-metric-icon">⏱</span>
                        Avg Session
                    </div>
                    <div className={sessionClass}>{formatSession(avgSessionSec)}</div>
                    {sparkSession.length > 1 && <Sparkline values={sparkSession} color={GA_BLUE} />}
                </div>

                {/* Bounce Rate — traffic light */}
                <div className="ga-metric-card ga-metric-card-primary">
                    <div className="ga-metric-label">
                        <span className="ga-metric-icon">↩</span>
                        Bounce Rate
                    </div>
                    <div className={bounceClass}>
                        {bounceText(bounceRate, avgSessionSec)}
                    </div>
                    {sparkBounce.length > 1 && <Sparkline values={sparkBounce} color={GA_BLUE} />}
                </div>

                {/* External listing link */}
                {listingUrl && (
                    <div className="ga-metric-card ga-link-card">
                        <a
                            href={listingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ga-listing-link"
                        >
                            View on website
                            <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13" aria-hidden="true" style={{ marginLeft: 5 }}>
                                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                                <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                            </svg>
                        </a>
                    </div>
                )}
            </div>

            {/* Monthly trend chart — only when we have 2+ months of data */}
            {ga4MonthlyChart && ga4MonthlyChart.monthCount >= 2 && (
                <div className="ga-subsection">
                    <div className="ga-subsection-title">
                        Monthly Trend
                        <span className="ga-chart-sublabel"> — last {ga4MonthlyChart.monthCount} {ga4MonthlyChart.monthCount === 1 ? 'month' : 'months'}</span>
                    </div>
                    <div className="ga-chart-wrap">
                        <Bar
                            data={{
                                labels: ga4MonthlyChart.labels,
                                datasets: [
                                    {
                                        label: 'Pageviews',
                                        data: ga4MonthlyChart.pageviews,
                                        backgroundColor: 'rgba(66, 133, 244, 0.35)',
                                        borderColor: '#4285f4',
                                        borderWidth: 1.5,
                                        borderRadius: 4,
                                        yAxisID: 'y',
                                    },
                                    {
                                        label: 'Unique Users',
                                        data: ga4MonthlyChart.users,
                                        backgroundColor: 'rgba(251, 188, 4, 0.55)',
                                        borderColor: '#fbbc04',
                                        borderWidth: 1.5,
                                        borderRadius: 4,
                                        yAxisID: 'y1',
                                    },
                                ],
                            }}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                interaction: { mode: 'index', intersect: false },
                                plugins: {
                                    legend: { position: 'top', labels: { boxWidth: 12, font: { size: 11 } } },
                                    tooltip: {
                                        callbacks: {
                                            label: (ctx) => `${ctx.dataset.label}: ${formatNumber(ctx.parsed.y || 0)}`,
                                        },
                                    },
                                },
                                scales: {
                                    x: { grid: { display: false }, ticks: { font: { size: 10 } } },
                                    y: {
                                        position: 'left',
                                        beginAtZero: true,
                                        title: { display: true, text: 'Pageviews', font: { size: 10 } },
                                        ticks: { font: { size: 10 } },
                                    },
                                    y1: {
                                        position: 'right',
                                        beginAtZero: true,
                                        title: { display: true, text: 'Users', font: { size: 10 } },
                                        grid: { drawOnChartArea: false },
                                        ticks: { font: { size: 10 } },
                                    },
                                },
                            }}
                        />
                    </div>
                </div>
            )}
        </section>
    );
}

export default OverviewTab;
